import { updateRadioBtnStyles } from '../utils/ui.js';
import { QUALITY_MAP } from '../utils/formatters.js';

// Save Builds Logic

const BUILDS_STORAGE_KEY = 'gearsets_saved_builds';

const SLOTS = [
    'helmet', 'chest', 'pants', 'boots', 'weapon', 'ring',
    'trinket1', 'trinket2', 'chanfron', 'saddle', 'peytral'
];

// Global target for builds operations
let activeTargetSet = 'set1';

/**
 * Maps a list of gear slot configuration objects to an array of permanent IDs + Level + Quality.
 * We store: [item_id, quality, level] per slot.
 */
function getCurrentBuildConfigurations() {
    const build = {};
    const prefix = activeTargetSet;

    SLOTS.forEach(slot => {
        const nameSelect = document.getElementById(`${prefix}_${slot}_name`);
        const qSelect = document.getElementById(`${prefix}_${slot}_quality`);
        const lSelect = document.getElementById(`${prefix}_${slot}_level`);

        if (nameSelect && nameSelect.value) {
            const itemName = nameSelect.value;
            const quality = qSelect ? qSelect.value : 'legendary';
            const level = lSelect ? parseInt(lSelect.value, 10) : 40;

            // Look up ID from window.GEAR_DATA
            // gear_data is grouped by slot. For trinket1/trinket2, the data slot is 'trinket'
            const dataSlot = slot.startsWith('trinket') ? 'trinket' : slot;
            const itemsInSlot = window.GEAR_DATA[dataSlot] || [];

            const itemObj = itemsInSlot.find(i => i.name === itemName);
            if (itemObj && itemObj.id) {
                build[slot] = {
                    id: itemObj.id,
                    q: quality,
                    l: level
                };
            }
        }
    });

    return build;
}

/**
 * Loads a build into the targeted DOM set
 */
function loadBuild(buildData, buildId = null) {
    if (!buildData) {
        console.warn("loadBuild called with undefined or null buildData");
        return;
    }

    const prefix = activeTargetSet;

    try {
        // Automatically toggle active tab to 'Saved' mode
        const savedRadio = document.querySelector(`input[name="${prefix}_bulk_mode"][value="saved"]`);
        if (savedRadio) {
            savedRadio.checked = true;
            savedRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Sync the quick load selection dropdowns conditionally
        const gearSlots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots'];
        const trinketSlots = ['trinket1', 'trinket2'];
        const pitSlots = ['saddle', 'chanfron', 'peytral'];

        const hasGear = gearSlots.some(s => buildData[s] && buildData[s].id);
        const hasTrinket = trinketSlots.some(s => buildData[s] && buildData[s].id);
        const hasPit = pitSlots.some(s => buildData[s] && buildData[s].id);

        const dropdownConfigs = [
            { id: `${prefix}_quick_load`, active: hasGear },
            { id: `${prefix}_trinket_quick_load`, active: hasTrinket },
            { id: `${prefix}_pit_quick_load`, active: hasPit }
        ];

        dropdownConfigs.forEach(cfg => {
            const dropdown = document.getElementById(cfg.id);
            if (dropdown) {
                if (cfg.active) {
                    dropdown.value = buildId || '';
                    if (dropdown.value) {
                        dropdown.classList.add('has-selection');
                    } else {
                        dropdown.classList.remove('has-selection');
                    }
                } else if (!buildId) {
                    // Reset to empty if we are clearing the builds
                    dropdown.value = '';
                    dropdown.classList.remove('has-selection');
                }
            }
        });
    } catch (e) {
        console.error("Error updating Saved Builds UI state during loadBuild:", e);
    }

    const gearSlots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots'];
    const trinketSlots = ['trinket1', 'trinket2'];
    const pitSlots = ['saddle', 'chanfron', 'peytral'];

    const hasGear = gearSlots.some(s => buildData[s] && buildData[s].id);
    const hasTrinket = trinketSlots.some(s => buildData[s] && buildData[s].id);
    const hasPit = pitSlots.some(s => buildData[s] && buildData[s].id);

    // First clear current selections only for groups that exist in the loaded build
    SLOTS.forEach(slot => {
        const isGear = gearSlots.includes(slot);
        const isTrinket = trinketSlots.includes(slot);
        const isPit = pitSlots.includes(slot);

        if ((isGear && !hasGear) || (isTrinket && !hasTrinket) || (isPit && !hasPit)) {
            // Keep unchanged!
            return;
        }

        const nameSelect = document.getElementById(`${prefix}_${slot}_name`);
        const qSelect = document.getElementById(`${prefix}_${slot}_quality`);
        const lSelect = document.getElementById(`${prefix}_${slot}_level`);

        if (nameSelect) nameSelect.value = '';
        if (qSelect) qSelect.value = 'legendary';
        if (lSelect) lSelect.value = slot.startsWith('trinket') ? '35' : '40';
    });

    // Clear bulk selections only for groups that exist in the loaded build
    const bulkSelectorsToReset = [];
    if (hasGear) {
        bulkSelectorsToReset.push(
            { id: `${prefix}_full_set`, val: '' },
            { id: `${prefix}_bulk_gear_quality`, val: 'legendary' },
            { id: `${prefix}_bulk_gear_level`, val: '40' },
            { id: `${prefix}_gear_prebuild`, val: '' }
        );
    }
    if (hasTrinket) {
        bulkSelectorsToReset.push(
            { id: `${prefix}_bulk_trinket_name`, val: '' },
            { id: `${prefix}_bulk_trinket_quality`, val: 'legendary' },
            { id: `${prefix}_bulk_trinket_level`, val: '35' },
            { id: `${prefix}_trinket_prebuild`, val: '' }
        );
    }
    if (hasPit) {
        bulkSelectorsToReset.push(
            { id: `${prefix}_pit_set`, val: '' },
            { id: `${prefix}_bulk_pit_quality`, val: 'legendary' },
            { id: `${prefix}_bulk_pit_level`, val: '40' },
            { id: `${prefix}_pit_prebuild`, val: '' }
        );
    }

    bulkSelectorsToReset.forEach(cfg => {
        const el = document.getElementById(cfg.id);
        if (el) {
            el.value = cfg.val;
            el.classList.remove('has-selection');
        }
    });

    // Apply new selections
    for (const [slot, itemData] of Object.entries(buildData)) {
        if (!itemData.id) continue;

        const dataSlot = slot.startsWith('trinket') ? 'trinket' : slot;
        const itemsInSlot = window.GEAR_DATA[dataSlot] || [];

        const itemObj = itemsInSlot.find(i => i.id === itemData.id);
        if (itemObj) {
            const nameSelect = document.getElementById(`${prefix}_${slot}_name`);
            const qSelect = document.getElementById(`${prefix}_${slot}_quality`);
            const lSelect = document.getElementById(`${prefix}_${slot}_level`);

            if (nameSelect) nameSelect.value = itemObj.name;
            if (qSelect) qSelect.value = itemData.q;
            if (lSelect) lSelect.value = itemData.l;

            // Dispatch change events so other UI elements (like visuals) update
            if (nameSelect) nameSelect.dispatchEvent(new Event('change', { bubbles: true }));
            if (qSelect) qSelect.dispatchEvent(new Event('change', { bubbles: true }));
            if (lSelect) lSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}

function getSavedBuilds() {
    const stored = localStorage.getItem(BUILDS_STORAGE_KEY);
    if (!stored) return [];
    try {
        return JSON.parse(stored) || [];
    } catch {
        return [];
    }
}

function saveBuilds(buildsArray) {
    localStorage.setItem(BUILDS_STORAGE_KEY, JSON.stringify(buildsArray));
}

function renderBuildsList() {
    const container = document.getElementById('builds_results');
    const builds = getSavedBuilds();

    // Rebuild options HTML
    let optionsHtml = '<option value="">Select a Build</option>';
    builds.forEach((b, index) => {
        optionsHtml += `<option value="${b.id}">${index + 1}. ${b.name}</option>`;
    });

    ['set1', 'set2'].forEach(prefix => {
        const selectTypes = ['', '_trinket', '_pit'];
        selectTypes.forEach(type => {
            const dropdown = document.getElementById(`${prefix}${type}_quick_load`);
            const dropdownContainer = document.getElementById(`${prefix}${type}_quick_load_container`);

            if (dropdownContainer) {
                if (builds.length === 0) {
                    dropdownContainer.style.display = 'none';
                } else {
                    dropdownContainer.style.display = 'flex';
                }
            }

            if (dropdown) {
                dropdown.innerHTML = optionsHtml;
            }
        });
    });

    if (!container) return;

    if (builds.length === 0) {
        container.innerHTML = '<p style="text-align: center; color: var(--text-muted); padding: 2rem;">No saved builds yet. Configure some gear and click save!</p>';
        return;
    }

    container.innerHTML = '';

    builds.forEach((b, index) => {
        let totalItems = 0;
        const classesPresent = new Set();
        let hasDragonStat = false;
        let hasPitGear = false;
        
        if (b.data) {
            Object.entries(b.data).forEach(([slot, itemData]) => {
                if (itemData && itemData.id) {
                    totalItems++;
                    
                    if (['saddle', 'chanfron', 'peytral'].includes(slot)) {
                        hasPitGear = true;
                    }

                    const dataSlot = slot.startsWith('trinket') ? 'trinket' : slot;
                    const itemsInSlot = window.GEAR_DATA ? window.GEAR_DATA[dataSlot] : [];
                    if (itemsInSlot) {
                        const itemObj = itemsInSlot.find(i => i.id === itemData.id);
                        if (itemObj) {
                            if (itemObj.stats) {
                                Object.keys(itemObj.stats).forEach(key => {
                                    if (!['saddle', 'chanfron', 'peytral'].includes(slot) && key.startsWith('Dragon')) {
                                        hasDragonStat = true;
                                    }
                                });
                            }
                            if (!['saddle', 'chanfron', 'peytral'].includes(slot)) {
                                if (itemObj.class && itemObj.class !== 'All' && itemObj.class !== 'Dragon' && itemObj.class !== 'SOP' && itemObj.class !== 'Troop') {
                                    classesPresent.add(itemObj.class);
                                }
                            }
                        }
                    }
                }
            });
        }

        const card = document.createElement('div');
        card.className = 'build-card';

        const topRow = document.createElement('div');
        topRow.className = 'build-card-top';

        const infoDiv = document.createElement('div');
        infoDiv.className = 'build-info';

        const nameDiv = document.createElement('div');
        nameDiv.className = 'build-name';
        nameDiv.textContent = `${index + 1}. ${b.name}`;

        const metaDiv = document.createElement('div');
        metaDiv.className = 'build-meta';
        
        const countBadge = document.createElement('span');
        countBadge.className = 'build-badge';
        countBadge.textContent = `${totalItems} Item${totalItems === 1 ? '' : 's'}`;
        metaDiv.appendChild(countBadge);

        if (hasDragonStat || classesPresent.size > 0 || hasPitGear) {
            const CLASS_ABBREV = {
                'Ranged': 'Rng',
                'Hunter': 'Rng',
                'Infantry': 'Inf',
                'Cavalry': 'Cav'
            };
            
            const sortedClasses = Array.from(classesPresent).sort();
            let tags = sortedClasses.map(c => CLASS_ABBREV[c] || c.substring(0, 3));
            
            if (hasDragonStat) {
                tags.unshift('Drag');
            }
            
            if (hasPitGear) tags.push('Pit');
            
            tags.forEach(tag => {
                const badge = document.createElement('span');
                badge.className = 'build-badge';
                badge.textContent = tag;
                metaDiv.appendChild(badge);
            });
        }

        infoDiv.appendChild(nameDiv);
        infoDiv.appendChild(metaDiv);

        let updatedTime = b.updatedAt;
        if (!updatedTime && b.id && b.id.startsWith('b_')) {
            const tsStr = b.id.substring(2);
            const ts = parseInt(tsStr, 10);
            if (!isNaN(ts) && ts > 1000000000000) updatedTime = ts;
        }

        const actionsDiv = document.createElement('div');
        actionsDiv.className = 'build-actions-dropdown-container';

        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'build-icon-btn';
        toggleBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle></svg>';
        toggleBtn.setAttribute('aria-label', 'Options');
        toggleBtn.title = 'Options';

        const menuDiv = document.createElement('div');
        menuDiv.className = 'build-dropdown-menu';
        menuDiv.style.display = 'none';

        toggleBtn.onclick = (e) => {
            e.stopPropagation();
            const isVisible = menuDiv.style.display === 'flex';
            // Hide all other open menus
            document.querySelectorAll('.build-dropdown-menu').forEach(menu => menu.style.display = 'none');
            menuDiv.style.display = isVisible ? 'none' : 'flex';
        };

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!actionsDiv.contains(e.target)) {
                menuDiv.style.display = 'none';
            }
        });

        const equipBtn = document.createElement('button');
        equipBtn.className = 'build-dropdown-item';
        equipBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 0.5rem;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> <span>Equip</span>';
        equipBtn.onclick = () => {
            loadBuild(b.data, b.id);

            const span = equipBtn.querySelector('span');
            const originalText = span.textContent;
            span.textContent = 'Equipped!';
            equipBtn.style.color = 'var(--positive-color)';

            setTimeout(() => {
                span.textContent = originalText;
                equipBtn.style.color = '';
            }, 1000);
            
            setTimeout(() => { menuDiv.style.display = 'none'; }, 200);
        };

        const targetName = activeTargetSet === 'set1' ? 'Baseline' : 'Target';
        const overBtn = document.createElement('button');
        overBtn.className = 'build-dropdown-item warning';
        overBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 0.5rem;"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg> <span>Update</span>';
        overBtn.title = `Update saved slots with currently selected ${targetName} gear`;
        overBtn.onclick = () => {
            const currentData = getCurrentBuildConfigurations();

            if (Object.keys(currentData).length === 0) {
                alert("You have no gear selected! Cannot overwrite with an empty build.");
                menuDiv.style.display = 'none';
                return;
            }

            if (confirm(`Overwrite "${b.name}" with your currently selected ${targetName} gear? (Only selected slots will be updated)`)) {
                b.data = { ...b.data, ...currentData };
                b.updatedAt = Date.now();
                saveBuilds(builds);

                const span = overBtn.querySelector('span');
                span.textContent = 'Updated!';
                overBtn.style.color = 'var(--q-epic)';
                overBtn.querySelector('svg').outerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 0.5rem;"><polyline points="20 6 9 17 4 12"></polyline></svg>';

                setTimeout(() => {
                    renderBuildsList();
                }, 1000);
            } else {
                menuDiv.style.display = 'none';
            }
        };

        const delBtn = document.createElement('button');
        delBtn.className = 'build-dropdown-item danger';
        delBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 0.5rem;"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> <span>Delete</span>';
        delBtn.onclick = () => {
            if (confirm(`Delete "${b.name}"?`)) {
                const newBuilds = builds.filter(x => x.id !== b.id);
                saveBuilds(newBuilds);
                renderBuildsList();
            } else {
                menuDiv.style.display = 'none';
            }
        };

        menuDiv.appendChild(equipBtn);
        menuDiv.appendChild(overBtn);
        menuDiv.appendChild(delBtn);

        actionsDiv.appendChild(toggleBtn);
        actionsDiv.appendChild(menuDiv);

        topRow.appendChild(infoDiv);
        topRow.appendChild(actionsDiv);
        card.appendChild(topRow);

        if (updatedTime) {
            const timeDiv = document.createElement('div');
            timeDiv.className = 'build-time';
            const date = new Date(updatedTime);
            timeDiv.textContent = `Last Updated: ${date.toLocaleString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })}`;
            card.appendChild(timeDiv);
        }

        container.appendChild(card);
    });
}

function copyExportUrl(urlString) {
    const exportBtn = document.getElementById('export-builds-btn');

    navigator.clipboard.writeText(urlString).then(() => {
        if (exportBtn) {
            exportBtn.textContent = 'Copied to Clipboard!';
            exportBtn.style.color = 'var(--positive-color)';
            exportBtn.style.borderColor = 'var(--positive-color)';

            setTimeout(() => {
                exportBtn.textContent = 'Export Builds';
                exportBtn.style.color = '';
                exportBtn.style.borderColor = '';
            }, 2000);
        }
    }).catch(err => {
        console.error("Clipboard error", err);
        prompt("Copy this link manually:", urlString);
    });
}

function showExportSubsetModal(allBuilds) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop visible';
    overlay.style.zIndex = '10011';

    const modalContent = document.createElement('div');
    modalContent.className = 'modal-content';
    modalContent.style.textAlign = 'left';
    modalContent.style.maxWidth = '400px';
    modalContent.style.padding = '2rem 1.5rem';

    const title = document.createElement('h2');
    title.textContent = 'Export Selection';
    title.style.marginBottom = '1rem';
    title.style.color = 'var(--text-color)';
    title.style.textAlign = 'center';

    const text = document.createElement('p');
    text.innerHTML = `Select up to 6 builds to export:`;
    text.style.marginBottom = '1.5rem';
    text.style.color = 'var(--text-color)';
    text.style.lineHeight = '1.5';
    text.style.textAlign = 'center';

    const checkboxContainer = document.createElement('div');
    checkboxContainer.style.marginBottom = '1.5rem';
    checkboxContainer.style.maxHeight = '200px';
    checkboxContainer.style.overflowY = 'auto';
    checkboxContainer.style.display = 'flex';
    checkboxContainer.style.flexDirection = 'column';
    checkboxContainer.style.gap = '0.5rem';
    checkboxContainer.style.padding = '0.75rem';
    checkboxContainer.style.background = 'var(--bg-secondary)';
    checkboxContainer.style.borderRadius = 'var(--border-radius)';
    checkboxContainer.style.border = '1px solid var(--border-color)';
    checkboxContainer.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.1)';

    const errorMsg = document.createElement('div');
    errorMsg.style.color = 'var(--accent-color)'; // Using the theme's yellow accent
    errorMsg.style.marginBottom = '1rem';
    errorMsg.style.display = 'none';
    errorMsg.style.textAlign = 'center';
    errorMsg.style.fontSize = '0.9rem';

    const checkboxes = [];
    allBuilds.forEach((b, index) => {
        const label = document.createElement('label');
        label.style.display = 'flex';
        label.style.alignItems = 'center';
        label.style.gap = '0.5rem';
        label.style.cursor = 'pointer';

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = b.id;
        cb.checked = true;

        const span = document.createElement('span');
        span.textContent = `${index + 1}. ${b.name}`;

        label.appendChild(cb);
        label.appendChild(span);
        checkboxContainer.appendChild(label);
        checkboxes.push(cb);
    });

    const btnGroup = document.createElement('div');
    btnGroup.style.display = 'flex';
    btnGroup.style.gap = '0.75rem';
    btnGroup.style.justifyContent = 'center';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'secondary-btn';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.flex = '1';
    cancelBtn.style.display = 'flex';
    cancelBtn.style.alignItems = 'center';
    cancelBtn.style.justifyContent = 'center';
    
    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'submit-btn';
    confirmBtn.textContent = 'Generate Link';
    confirmBtn.style.flex = '1';

    btnGroup.appendChild(cancelBtn);
    btnGroup.appendChild(confirmBtn);

    modalContent.appendChild(title);
    modalContent.appendChild(text);
    modalContent.appendChild(checkboxContainer);
    modalContent.appendChild(errorMsg);
    modalContent.appendChild(btnGroup);
    overlay.appendChild(modalContent);
    document.body.appendChild(overlay);

    const cleanup = () => {
        if (document.body.contains(overlay)) {
            document.body.removeChild(overlay);
        }
    };

    cancelBtn.onclick = cleanup;

    confirmBtn.onclick = () => {
        errorMsg.style.display = 'none';
        
        const selectedIds = checkboxes.filter(c => c.checked).map(c => c.value);
        if (selectedIds.length === 0) {
            errorMsg.textContent = 'Please select at least one build.';
            errorMsg.style.display = 'block';
            return;
        }
        
        if (selectedIds.length > 6) {
            errorMsg.textContent = `You have selected ${selectedIds.length} builds. Please select 6 or fewer builds to export.`;
            errorMsg.style.display = 'block';
            return;
        }

        const selectedBuilds = allBuilds.filter(b => selectedIds.includes(b.id));
        
        const tinyData = selectedBuilds.map(b => {
            let lastIndex = -1;
            const tempArray = SLOTS.map((slot, i) => {
                const d = b.data ? b.data[slot] : b[slot];
                if (d) lastIndex = i;
                if (!d) return 0;
                const qIndex = QUALITY_MAP.indexOf(d.q);
                return [d.id, qIndex !== -1 ? qIndex : 5, d.l];
            });
            const slotArray = lastIndex >= 0 ? tempArray.slice(0, lastIndex + 1) : [];
            return [b.name, slotArray];
        });

        const jsonStr = JSON.stringify(tinyData);
        const compressed = LZString.compressToEncodedURIComponent(jsonStr);

        const url = new URL(window.location.href);
        url.searchParams.set('builds', compressed);
        const finalUrl = url.toString();
        
        copyExportUrl(finalUrl);
        cleanup();
    };
    
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            cleanup();
        }
    });
}

function exportBuildsToLink() {
    const builds = getSavedBuilds();
    if (builds.length === 0) {
        alert("No builds to export!");
        return;
    }

    if (builds.length > 20) {
        alert(`You currently have ${builds.length} builds saved. Please delete some builds to be at or below the 20 build limit before exporting.`);
        return;
    }

    // Format: [[Name, [ [id, qInt, l], 0, [id, qInt, l]... ] ]]
    // We strip trailing zeros to save massive space for incomplete builds
    const tinyData = builds.map(b => {
        let lastIndex = -1;
        const tempArray = SLOTS.map((slot, i) => {
            const d = b.data ? b.data[slot] : b[slot];
            if (d) lastIndex = i;
            if (!d) return 0;
            const qIndex = QUALITY_MAP.indexOf(d.q);
            return [d.id, qIndex !== -1 ? qIndex : 5, d.l]; // Default index 5 is legendary
        });

        // Only keep the array up to the last equipped item to save space
        const slotArray = lastIndex >= 0 ? tempArray.slice(0, lastIndex + 1) : [];
        return [b.name, slotArray];
    });

    const jsonStr = JSON.stringify(tinyData);
    const compressed = LZString.compressToEncodedURIComponent(jsonStr);

    const url = new URL(window.location.href);
    url.searchParams.set('builds', compressed);
    const finalUrl = url.toString();

    if (builds.length > 6) {
        showExportSubsetModal(builds);
        return;
    }

    copyExportUrl(finalUrl);
}

function checkAndProcessImportedBuilds() {
    const params = new URLSearchParams(window.location.search);
    const bHash = params.get('builds');
    if (!bHash) return;

    try {
        const decompressed = LZString.decompressFromEncodedURIComponent(bHash);
        const parsed = JSON.parse(decompressed);

        if (!parsed || !Array.isArray(parsed)) throw new Error("Invalid Format");

        const importedBuilds = parsed.map(arr => {
            let id, name, slotsData;

            // Handle backward compatibility dynamically
            if (arr.length === 3 && typeof arr[0] === 'string' && arr[0].startsWith('b_')) {
                [id, name, slotsData] = arr;
            } else {
                id = 'b_' + Date.now() + Math.random().toString(36).substr(2, 5);
                name = arr[0];
                slotsData = arr[1];
            }

            let dataObj = {};
            if (Array.isArray(slotsData)) {
                // New highly compressed array format
                slotsData.forEach((v, i) => {
                    if (v && v.length === 3) {
                        dataObj[SLOTS[i]] = { id: v[0], q: QUALITY_MAP[v[1]] || 'legendary', l: v[2] };
                    }
                });
            } else {
                // Backwards compatibility for the brief period using object format
                dataObj = slotsData;
            }

            return { id, name, data: dataObj };
        });

        setTimeout(() => {
            const existing = getSavedBuilds();
            
            // Filter out imported builds that exactly match an existing build
            const uniqueImportedBuilds = importedBuilds.filter(importBuild => {
                return !existing.some(existingBuild => {
                    if (existingBuild.name !== importBuild.name) return false;
                    
                    let dataA = existingBuild.data;
                    if (!dataA) {
                        dataA = {};
                        SLOTS.forEach(slot => {
                            if (existingBuild[slot]) dataA[slot] = existingBuild[slot];
                        });
                    }
                    
                    const dataB = importBuild.data || {};
                    const keysA = Object.keys(dataA);
                    const keysB = Object.keys(dataB);
                    
                    if (keysA.length !== keysB.length) return false;
                    
                    for (const slot of SLOTS) {
                        const itemA = dataA[slot];
                        const itemB = dataB[slot];
                        
                        if (!itemA && !itemB) continue;
                        if (!itemA || !itemB) return false;
                        
                        if (String(itemA.id) !== String(itemB.id) || 
                            String(itemA.q || 'legendary').toLowerCase() !== String(itemB.q || 'legendary').toLowerCase() || 
                            String(itemA.l || 40) !== String(itemB.l || 40)) {
                            return false;
                        }
                    }
                    return true;
                });
            });

            if (uniqueImportedBuilds.length === 0) {
                // All imported builds already exist, clean up URL and skip prompt
                const url = new URL(window.location.href);
                url.searchParams.delete('builds');
                window.history.replaceState({}, document.title, url.toString());
                return;
            }

            const testMap = new Map();
            existing.forEach(b => testMap.set(b.id, b));
            uniqueImportedBuilds.forEach(b => testMap.set(b.id, b));
            const expectedTotalCount = testMap.size;

            const isOverLimit = expectedTotalCount > 20;

            const overlay = document.createElement('div');
            overlay.className = 'modal-backdrop visible';
            overlay.style.zIndex = '10011'; // Ensure it's on top of everything

            const modalContent = document.createElement('div');
            modalContent.className = 'modal-content';
            modalContent.style.textAlign = 'center';
            modalContent.style.maxWidth = '400px';
            modalContent.style.padding = '2rem 1.5rem';

            const title = document.createElement('h2');
            title.textContent = isOverLimit ? 'Storage Limit Exceeded' : 'Import Builds';
            title.style.marginBottom = '1rem';
            title.style.color = isOverLimit ? 'var(--danger-color)' : '';

            const text = document.createElement('p');
            if (isOverLimit) {
                text.innerHTML = `This link contains <strong>${uniqueImportedBuilds.length}</strong> new saved builds, which would put you at <strong>${expectedTotalCount}</strong> total. You can only store 20 builds max.<br><br>Please remove some builds before syncing to make room.`;
            } else {
                text.innerHTML = `This link contains <strong>${uniqueImportedBuilds.length}</strong> new saved build${uniqueImportedBuilds.length === 1 ? '' : 's'}.<br><br>Do you want to import them and sync your existing list?`;
            }
            text.style.marginBottom = '1.5rem';
            text.style.color = 'var(--text-color)';
            text.style.lineHeight = '1.5';

            const btnGroup = document.createElement('div');
            btnGroup.style.display = 'flex';
            btnGroup.style.gap = '0.75rem';
            btnGroup.style.justifyContent = 'center';
            btnGroup.style.flexWrap = 'wrap';

            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'secondary-btn';
            cancelBtn.textContent = isOverLimit ? 'Close' : 'Cancel';
            cancelBtn.style.flex = '1';
            cancelBtn.style.alignItems = 'center';
            cancelBtn.style.justifyContent = 'center';

            btnGroup.appendChild(cancelBtn);

            let confirmBtn;
            if (!isOverLimit) {
                confirmBtn = document.createElement('button');
                confirmBtn.className = 'submit-btn';
                confirmBtn.textContent = 'Sync';
                confirmBtn.style.flex = '1';
                btnGroup.appendChild(confirmBtn);
            }

            modalContent.appendChild(title);
            modalContent.appendChild(text);
            modalContent.appendChild(btnGroup);
            overlay.appendChild(modalContent);
            document.body.appendChild(overlay);

            const cleanup = () => {
                document.body.removeChild(overlay);
                // Clean up URL
                const url = new URL(window.location.href);
                url.searchParams.delete('builds');
                window.history.replaceState({}, document.title, url.toString());
            };

            cancelBtn.onclick = cleanup;

            if (confirmBtn) {
                confirmBtn.onclick = () => {
                    saveBuilds(Array.from(testMap.values()));
                    renderBuildsList();
                    cleanup();
                };
            }
        }, 500);

    } catch (e) {
        console.error("Failed to import builds", e);
        alert("Failed to parse import link.");
    }
}

export function initBuilds() {
    const radioInputs = document.querySelectorAll('input[name="builds_target_toggle"]');
    const saveBtn = document.getElementById('save-current-build-btn');

    function updateTargetState() {
        updateRadioBtnStyles('#builds_target_group label');
        const container = document.getElementById('builds_target_group');
        if (container) {
            const wrapper = container.closest('.rankings-container') || document.body;
            wrapper.classList.remove('target-set1', 'target-set2');
            wrapper.classList.add('target-' + activeTargetSet);
        }
        renderBuildsList(); // Re-render to update the hover title of Overwrite buttons
    }

    if (radioInputs.length > 0) {
        radioInputs.forEach(radio => {
            radio.addEventListener('change', (e) => {
                activeTargetSet = e.target.value;
                updateTargetState();
            });
        });
    }

    updateTargetState();
    renderBuildsList();
    checkAndProcessImportedBuilds();

    // Hook up Quick Load Dropdowns
    ['set1', 'set2'].forEach(prefix => {
        const selectTypes = ['', '_trinket', '_pit'];
        selectTypes.forEach(type => {
            const selectElement = document.getElementById(`${prefix}${type}_quick_load`);
            if (selectElement) {
                selectElement.addEventListener('change', (e) => {
                    const selectedId = e.target.value;
                    
                    // Clear selection border if empty selection
                    if (!selectedId) {
                        selectTypes.forEach(t => {
                            const sel = document.getElementById(`${prefix}${t}_quick_load`);
                            if (sel) {
                                sel.value = '';
                                sel.classList.remove('has-selection');
                            }
                        });
                        return;
                    }

                    const builds = getSavedBuilds();
                    const buildToLoad = builds.find(b => b.id === selectedId);
                    
                    if (buildToLoad && buildToLoad.data) {
                        const buildData = buildToLoad.data;
                        const gearSlots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots'];
                        const trinketSlots = ['trinket1', 'trinket2'];
                        const pitSlots = ['saddle', 'chanfron', 'peytral'];

                        const hasGear = gearSlots.some(s => buildData[s] && buildData[s].id);
                        const hasTrinket = trinketSlots.some(s => buildData[s] && buildData[s].id);
                        const hasPit = pitSlots.some(s => buildData[s] && buildData[s].id);

                        const dropdownConfigs = [
                            { id: `${prefix}_quick_load`, active: hasGear },
                            { id: `${prefix}_trinket_quick_load`, active: hasTrinket },
                            { id: `${prefix}_pit_quick_load`, active: hasPit }
                        ];

                        dropdownConfigs.forEach(cfg => {
                            const sel = document.getElementById(cfg.id);
                            if (sel) {
                                if (cfg.active) {
                                    sel.value = selectedId;
                                    sel.classList.add('has-selection');
                                } else {
                                    sel.value = '';
                                    sel.classList.remove('has-selection');
                                }
                            }
                        });

                        // Temporarily change the active prefix for the generic load function to work properly locally
                        const originalPrefix = activeTargetSet;
                        activeTargetSet = prefix;
                        loadBuild(buildData, buildToLoad.id);
                        activeTargetSet = originalPrefix;
                    }
                });
            }
        });
    });
    /**
     * Helper function to handle the build saving flow, used by both
     * the drawer save button and the column-specific save buttons.
     */
    function handleSaveBuildFlow(btnElement, targetPrefix) {
        const builds = getSavedBuilds();
        if (builds.length >= 20) {
            alert("You have reached the maximum of 20 saved builds.");
            return;
        }

        // Temporarily swap active target to get specific column configurations
        const originalPrefix = activeTargetSet;
        activeTargetSet = targetPrefix;
        const currentData = getCurrentBuildConfigurations();
        activeTargetSet = originalPrefix;

        if (Object.keys(currentData).length === 0) {
            alert("You have no gear selected to save!");
            return;
        }

        const bName = prompt("Enter a name for this build (max 24 characters):", "New Build");
        if (!bName) return; 

        const cleanName = bName.trim().substring(0, 24);
        if (!cleanName) return;

        const newBuild = {
            id: 'b_' + Date.now(),
            name: cleanName,
            data: currentData,
            updatedAt: Date.now()
        };

        builds.push(newBuild);
        saveBuilds(builds);
        renderBuildsList();
        
        // Success animation logic
        if (btnElement) {
            const originalText = btnElement.innerHTML;
            btnElement.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.35rem; margin-top: -2px;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> Saved!`;
            setTimeout(() => {
                btnElement.innerHTML = originalText;
            }, 2000);
        }
    }

    // Hook up Save Column Buttons
    const saveColumnBtns = document.querySelectorAll('.save-column-btn');
    if (saveColumnBtns.length > 0) {
        saveColumnBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                handleSaveBuildFlow(btn, e.currentTarget.dataset.prefix);
            });
        });
    }

    if (saveBtn) {
        saveBtn.addEventListener('click', (e) => {
            handleSaveBuildFlow(e.currentTarget, activeTargetSet);
        });
    }


    const exportBtn = document.getElementById('export-builds-btn');
    if (exportBtn) {
        exportBtn.addEventListener('click', exportBuildsToLink);
    }

    // Builds Guide Modal Toggle
    const infoBtn = document.getElementById('builds-info-btn');
    const buildsModal = document.getElementById('builds-guide-modal');
    // Using querySelector to find the close button securely within the modal
    const closeGuideBtn = buildsModal ? buildsModal.querySelector('.modal-close-btn') : null;

    if (infoBtn && buildsModal) {
        infoBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            buildsModal.classList.add('visible');
        });
    }

    if (closeGuideBtn && buildsModal) {
        closeGuideBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            buildsModal.classList.remove('visible');
        });
    }

    // Close guide when clicking outside the modal content
    const dismissGuide = (e) => {
        if (buildsModal && buildsModal.classList.contains('visible')) {
            const modalContent = buildsModal.querySelector('.modal-content');
            const isClickInside = modalContent && modalContent.contains(e.target);
            const isClickOnInfoBtn = infoBtn && infoBtn.contains(e.target);

            if (!isClickInside && !isClickOnInfoBtn) {
                buildsModal.classList.remove('visible');
            }
        }
    };

    document.documentElement.addEventListener('click', dismissGuide);
}
